/** Composición guardada del cotizador en la ficha del requerimiento. */
import { SectionCard } from './SectionCard';
import type { RequirementQuoteSnapshot } from '@/services/commercialService';
import { useMonetaryPrecision } from '@/state/MonetaryPrecisionContext';

function toNumber(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function RequirementCompositionCard({
  snapshot,
}: {
  snapshot: RequirementQuoteSnapshot;
}) {
  const precision = useMonetaryPrecision();
  const currency = snapshot.currency ?? 'PEN';
  const lines = snapshot.lines ?? [];
  const subtotal = toNumber(snapshot.subtotal) ?? 0;
  const discountAmount = toNumber(snapshot.discount_amount) ?? 0;
  // Los snapshots v1 guardaban el total con IGV; el cotizador vigente no
  // calcula impuesto. El neto se deriva siempre de subtotal y descuento.
  const totalAmount = subtotal - discountAmount;
  const discountPercent = toNumber(snapshot.discount_percent) ?? 0;
  return (
    <SectionCard title="Composición del cotizador">
      {lines.length === 0 ? (
        <p className="text-sm text-text-secondary">Sin líneas guardadas.</p>
      ) : (
        <ul className="space-y-2">
          {lines.map((line, index) => (
            <li
              key={`${line.master_item_id ?? line.code}-${index}`}
              className="rounded-xl bg-bg-tertiary px-4 py-2"
            >
              <p className="break-words text-sm font-semibold text-text-primary">
                {line.code ? `${line.code} · ` : ''}
                {line.name || 'Línea'}
              </p>
              <p className="text-xs tabular-nums text-text-secondary">
                {line.quantity} × {precision.format('quotation_items', 'unit_price', toNumber(line.unit_price), currency)} ={' '}
                {precision.format('quotation_items', 'total', toNumber(line.line_total), currency)}
              </p>
            </li>
          ))}
        </ul>
      )}
      <dl className="mt-3 divide-y divide-border-light text-sm">
        <div className="flex items-center justify-between gap-3 py-2">
          <dt className="text-text-tertiary">Subtotal</dt>
          <dd className="tabular-nums text-text-primary">
            {precision.format('quotations', 'subtotal', subtotal, currency)}
          </dd>
        </div>
        {discountAmount > 0 && (
          <div className="flex items-center justify-between gap-3 py-2">
            <dt className="text-text-tertiary">
              Descuento{discountPercent > 0 ? ` (${discountPercent} %)` : ''}
            </dt>
            <dd className="tabular-nums text-text-primary">
              {precision.format('quotations', 'discount', discountAmount, currency)}
            </dd>
          </div>
        )}
        <div className="flex items-center justify-between gap-3 py-2">
          <dt className="font-semibold text-text-primary">Total</dt>
          <dd className="font-semibold tabular-nums text-primary">
            {precision.format('quotations', 'total', totalAmount, currency)}
          </dd>
        </div>
      </dl>
    </SectionCard>
  );
}
